import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useAuthModal } from '../../contexts/AuthModalContext';
import useWebPush from '../../hooks/useWebPush';
import { PUSH_STATUS, getPushStatus, isPushEnabledLocally } from '../../services/webPush';
import HelpModal from '../PageHelp/HelpModal';
import NotificationsModal from '../NotificationsModal/NotificationsModal';
import useHeaderNotifications from './useHeaderNotifications';
import {
  markNotificationsSeen,
  readNotifSeenAt,
  unseenCount,
} from './notificationSeen';
import '../../styles/public.css';

/**
 * Top chrome for public pages and PartnerPortal.
 * Public layout passes showSectionLinks={false} (Events/Places live in bottom nav).
 * PartnerPortal / Game Maker keep the default section links. My calendar lives on Profile.
 * Dark Mode toggle lives on Profile Appearance only (not in this header).
 * Page help (`?`) is always available. Signed-in users also get notifications (bell + modal).
 */
const PublicNav = ({
  showSectionLinks = true,
  notices = [],
  showInstallNotice = false,
  noticeCount = 0,
  onDismissNotices,
  installOffer = null,
}) => {
  const { currentUser, role, signOut, authLoading, isAdmin } = useAuth();
  const { openAuthModal } = useAuthModal();
  const location = useLocation();
  const [notifOpen, setNotifOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const {
    items,
    loading: notifLoading,
    respondFriend,
    dismissMemory,
    openMemory,
    openChat,
    dismissChat,
    busyId,
  } = useHeaderNotifications();
  useWebPush();
  const pushStatus = getPushStatus();
  const showPushCta =
    !isPushEnabledLocally() &&
    (pushStatus === PUSH_STATUS.DEFAULT || pushStatus === PUSH_STATUS.NEEDS_HOMESCREEN);

  const navLinkClass = (section) => {
    const { pathname } = location;
    const isEventsSection = section === 'events' && (pathname === '/' || pathname.startsWith('/events'));
    const isPlacesSection = section === 'places' && pathname.startsWith('/places');
    return isEventsSection || isPlacesSection ? 'nav-link active' : 'nav-link';
  };

  const uid = currentUser?.uid || '';
  const [seenState, setSeenState] = useState({ uid: '', at: 0 });
  const seenAt =
    seenState.uid === uid ? Math.max(seenState.at, readNotifSeenAt(uid)) : readNotifSeenAt(uid);
  const rawUnseen = unseenCount(items, seenAt) + (Number(noticeCount) || 0);
  const pendingCount = notifOpen ? 0 : rawUnseen;
  const showNotif = Boolean(currentUser) && !authLoading;

  useEffect(() => {
    if (!notifOpen || !uid) return;
    const next = markNotificationsSeen(uid, items);
    setSeenState((prev) => (prev.uid === uid && prev.at === next ? prev : { uid, at: next }));
  }, [notifOpen, uid, items]);

  const openNotif = useCallback(() => {
    if (uid) setSeenState({ uid, at: markNotificationsSeen(uid, items) });
    setNotifOpen(true);
  }, [uid, items]);
  const closeNotif = useCallback(() => {
    setNotifOpen(false);
    if (onDismissNotices) onDismissNotices();
  }, [onDismissNotices]);
  const openHelp = useCallback(() => setHelpOpen(true), []);
  const closeHelp = useCallback(() => setHelpOpen(false), []);

  const handleRespondFriend = useCallback(
    async (requestId, accept) => {
      try {
        await respondFriend(requestId, accept);
      } catch {
        /* reported in hook */
      }
    },
    [respondFriend]
  );

  const handleDismissMemory = useCallback(
    async (item) => {
      try {
        await dismissMemory(item);
      } catch {
        /* reported in hook */
      }
    },
    [dismissMemory]
  );

  return (
    <nav className={`public-nav${showSectionLinks ? '' : ' public-nav--slim'}`} aria-label="Site">
      <div className="public-nav-inner">
        <Link to="/" className="nav-logo">
          <span className="nav-logo-m2b">MAPTOBE</span>
          <span className="nav-logo-full">.com</span>
        </Link>

        {showSectionLinks && (
          <div className="nav-links">
            <Link to="/" className={navLinkClass('events')}>Events</Link>
            <Link to="/places" className={navLinkClass('places')}>Places</Link>
          </div>
        )}

        <div className="nav-auth">
          <button
            type="button"
            className="nav-help"
            onClick={openHelp}
            aria-label="Page help"
            aria-haspopup="dialog"
            aria-expanded={helpOpen}
          >
            <span className="nav-help__mark" aria-hidden="true">
              ?
            </span>
          </button>
          {!authLoading && (
            <>
              {currentUser ? (
                <>
                  {showNotif && (
                    <button
                      type="button"
                      className="nav-notif"
                      onClick={openNotif}
                      aria-label={
                        pendingCount > 0
                          ? `Notifications, ${pendingCount} pending`
                          : 'Notifications'
                      }
                      aria-haspopup="dialog"
                      aria-expanded={notifOpen}
                    >
                      <span className="nav-notif__icon" aria-hidden="true">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                          <path
                            d="M12 22a2.2 2.2 0 0 0 2.2-2.2h-4.4A2.2 2.2 0 0 0 12 22Zm7.2-6.2V11a7.2 7.2 0 0 0-5.4-7V3.2a1.8 1.8 0 1 0-3.6 0V4a7.2 7.2 0 0 0-5.4 7v4.8L3 17.6v1.2h18v-1.2l-1.8-1.8Z"
                            fill="currentColor"
                          />
                        </svg>
                      </span>
                      {pendingCount > 0 ? (
                        <span className="nav-notif__badge">{pendingCount > 9 ? '9+' : pendingCount}</span>
                      ) : null}
                    </button>
                  )}
                  {role === 'partner' && (
                    <Link to="/partner" className="nav-cta-btn">Partner tools</Link>
                  )}
                  {isAdmin && (
                    <Link to="/adminhub" className="nav-cta-btn">Admin</Link>
                  )}
                  <button type="button" className="nav-signout" onClick={signOut}>Sign out</button>
                </>
              ) : (
                <button
                  type="button"
                  className="nav-cta-btn"
                  onClick={() => openAuthModal({ mode: 'signup' })}
                >
                  Sign in / up
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {helpOpen ? (
        <HelpModal
          pathname={location.pathname}
          search={location.search}
          onClose={closeHelp}
        />
      ) : null}

      {notifOpen && showNotif ? (
        <NotificationsModal
          items={items}
          loading={notifLoading}
          busyId={busyId}
          onRespondFriend={handleRespondFriend}
          onOpenMemory={openMemory}
          onDismissMemory={handleDismissMemory}
          onOpenChat={openChat}
          onMarkChatRead={dismissChat}
          onClose={closeNotif}
          showPushCta={showPushCta}
          notices={notices}
          showInstallNotice={showInstallNotice}
          installOffer={installOffer}
        />
      ) : null}
    </nav>
  );
};

export default PublicNav;
